// Perfil.js — Casca da área "Minha Conta" redesenhada conforme mockups.
// Estrutura: breadcrumb → cabeçalho (avatar + nome + selo Autor) → sidebar + conteúdo.
// Navegação por abas via estado `activeTab` (mantém o evento legado `changePerfiltab`
// disparado por DashboardAutor/VendasAutor/PagarmeConfig).
import React, { useEffect, useState } from "react";
import { useAuth } from "../../services/context/AuthContext";
import useProfileData from "../../hooks/userProfileData";
import { useNavigate } from "react-router-dom";
import { message } from "../utils/toast";
import axios from "axios";

import Sidebar from "./Sidebar/Sidebar";
import Logout from "../login/Logout";

// Seções (GERAL)
import DadosPessoais from "./dadosPessoais/DadosPessoais";
import Creditos from "./creditos/Creditos";
import SaldoCreditos from "./creditos/SaldoCreditos";
import useBarraGrudada from "../../hooks/useBarraGrudada";
import MinhaLista from "./minhaLista/MinhaLista";
import MinhasAvaliacoes from "./avaliacoes/MinhasAvaliacoes";
import MeusLivros from "./meusLivros/MeusLivros";
import MeuClube from "./clube/MeuClube";
import MinhasComprasPerfil from "../compras/MinhasComprasPerfil";

// Seções (AUTOR)
// As três telas antigas (DashboardAutor, MeusLivrosAutor, VendasAutor) foram
// substituídas por AreaAutor. Os arquivos continuam no repositório por ora:
// eles guardam os cálculos que a tela nova consome pela API, e apagá-los junto
// com a mudança de navegação misturaria duas coisas num commit só.
import AreaAutor from "./areaAutor/AreaAutor";
import PagarmeConfig from "./pagarmeConfig/PagarmeConfig";
import SolicitarAutor from "./solicitarAutor/SolicitarAutor";
import MarcaPadrao from "../ui/MarcaPadrao";

// Rótulo do breadcrumb por aba
// "Métodos de Pagamento" saiu junto com o cartão: o site aceita somente PIX,
// que não exige nada cadastrado previamente.
const TAB_LABELS = {
  dados: "Meu Perfil",
  // As duas viraram SUB-ABAS de "Dados Pessoais" em 11/09/2026 (ver
  // dadosPessoais/DadosPessoais.js). As chaves continuam respondendo porque
  // telas antigas disparam `changePerfiltab` com elas — o checkout manda
  // cadastrar endereço, por exemplo.
  enderecos: "Endereços",
  seguranca: "Senha e Segurança",
  // A aba "Insigne Coins" saiu em 10/09/2026: saldo virou estado do cabeçalho,
  // não assunto de aba (ver SaldoCreditos). A CHAVE continua respondendo porque
  // telas antigas ainda disparam `changePerfiltab` com ela — cair no `default`
  // levaria a pessoa a "Meu Perfil" sem explicação.
  coins: "Loja de Créditos",
  creditos: "Loja de Créditos",
  // A CHAVE continua "meus-livros"; só o rótulo mudou. Telas antigas disparam
  // `changePerfiltab` com ela, e renomeá-la quebraria esses atalhos em
  // silêncio: o evento cairia no `default` e a pessoa pararia em "Meu Perfil".
  "meus-livros": "E-books",
  clube: "Clube Insigne",
  "minha-lista": "Minha Lista",
  avaliacoes: "Avaliações",
  "area-autor": "Área do Autor",
  // Rótulos legados: telas antigas ainda disparam `changePerfiltab` com estas
  // chaves, e todas caem na Área do Autor.
  "dashboard-autor": "Área do Autor",
  "livros-autor": "Área do Autor",
  vendas: "Área do Autor",
  "pagarme-config": "Configurar Pagar.me",
  "solicitar-autor": "Solicitar ser Autor",
  "meus-pedidos": "Registro de Compras",
};

const resolveFoto = (data, API_URL) => {
  const f = data?.image || data?.foto || data?.imagem || data?.avatar || null;
  if (!f) return null;
  return /^https?:\/\//i.test(f) ? f : `${API_URL}public/${f}`;
};

const Perfil = ({ handleLogout }) => {
  const { user, loading: authLoading, updateUser } = useAuth();
  const { profileData, loading: profileLoading, fetchProfileData } = useProfileData();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dados");
  const [promocaoLoading, setPromocaoLoading] = useState(false);
  // O estado do Clube, vindo da MESMA requisição dos saldos (SaldoCreditos).
  const [clube, setClube] = useState(null);
  const main = process.env.REACT_APP_MAIN;
  const API_URL = process.env.REACT_APP_API;

  const isLoading = authLoading || profileLoading;
  const userData = profileData || user;
  const isAuthor = ["autor", "admin"].includes(userData?.permissao);

  useEffect(() => {
    if (!isLoading && !user) navigate(main + "login");
  }, [isLoading, user, navigate, main]);

  // Mudança de aba via evento customizado (compatibilidade com telas legadas)
  useEffect(() => {
    // "coins" é a chave da aba antiga; hoje ela é a Loja de Créditos. Trocar
    // aqui, e não só renderizar a mesma tela, é o que acende o item certo na
    // barra lateral — com "coins" ativo, nenhum item ficaria marcado.
    const handleTabChange = (event) =>
      setActiveTab(event.detail === "coins" ? "creditos" : event.detail);
    window.addEventListener("changePerfiltab", handleTabChange);
    return () => window.removeEventListener("changePerfiltab", handleTabChange);
  }, []);

  // Auto-promoção a autor (preserva fluxo existente)
  const tornarSeAutor = async () => {
    if (promocaoLoading) return;
    try {
      setPromocaoLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}api/usuario-editora/tornar-se-autor`,
        {},
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      if (response.data.success) {
        message.success(response.data.message);
        if (updateUser) await updateUser();
        if (fetchProfileData) await fetchProfileData();
        setTimeout(() => setActiveTab("pagarme-config"), 1500);
      }
    } catch (error) {
      message.error(error.response?.data?.error || "Erro ao processar solicitação. Tente novamente.");
    } finally {
      setPromocaoLoading(false);
    }
  };

  // A barra do nome acompanha a rolagem e some quando o cabeçalho do site
  // volta — a regra inteira, com a sentinela e a medição, mora no hook, que é
  // o MESMO usado pela barra de busca da aba E-books. Duas cópias divergiriam
  // no primeiro ajuste, e as duas barras passariam a se comportar diferente.
  //
  // `margemNegativa: 16` é o `md:-mt-4` da barra: ela começa 16px acima da
  // sentinela. `quando: !isLoading` porque, enquanto o perfil carrega, o
  // cabeçalho nem existe no DOM para ser medido.
  const {
    barraRef: cabecalhoRef,
    sentinelaRef,
    altura: alturaCabecalho,
    esconder: esconderCabecalho,
  } = useBarraGrudada({ margemNegativa: 16, quando: !isLoading });

  if (isLoading) {
    return (
      <div className="bg-insigne-bg min-h-screen pt-[var(--nav-h)] text-center text-white/60">
        Carregando perfil...
      </div>
    );
  }

  const foto = resolveFoto(userData, API_URL);
  const primeiroNome = (userData?.nome || "").trim().split(" ")[0] || "Usuário";
  const seloFeminino = /a$/i.test(primeiroNome);

  const renderSection = () => {
    switch (activeTab) {
      // As três caem na MESMA seção; a chave só decide em qual sub-aba ela abre.
      case "dados": return <DadosPessoais onSair={() => navigate(main + "login")} />;
      case "enderecos": return <DadosPessoais inicial="enderecos" onSair={() => navigate(main + "login")} />;
      case "seguranca": return <DadosPessoais inicial="seguranca" onSair={() => navigate(main + "login")} />;
      case "coins":
      case "creditos": return <Creditos />;
      case "meus-livros": return <MeusLivros />;
      case "clube": return <MeuClube />;
      case "minha-lista": return <MinhaLista />;
      case "avaliacoes": return <MinhasAvaliacoes />;
      case "meus-pedidos": return <MinhasComprasPerfil />;
      // As três chaves antigas caem na mesma tela: um evento legado não pode
      // levar a lugar nenhum.
      case "area-autor":
      case "dashboard-autor":
      case "livros-autor":
      case "vendas":
        return isAuthor
          ? <AreaAutor onConfigurarPagarme={() => setActiveTab("pagarme-config")} />
          : <DadosPessoais onSair={() => navigate(main + "login")} />;
      case "pagarme-config": return isAuthor
        ? <PagarmeConfig userData={userData} loading={isLoading} />
        : <DadosPessoais onSair={() => navigate(main + "login")} />;
      case "solicitar-autor": return !isAuthor
        ? <SolicitarAutor userData={userData} loading={isLoading} />
        : <AreaAutor onConfigurarPagarme={() => setActiveTab("pagarme-config")} />;
      default: return <DadosPessoais onSair={() => navigate(main + "login")} />;
    }
  };

  return (
    // `overflow-x-clip` por causa do fundo de largura total da barra (abaixo):
    // ele mede 100vw, que inclui a barra de rolagem, e sem isto sobrariam uns
    // 15px de rolagem horizontal na página inteira. `clip`, e não `hidden`:
    // `hidden` criaria um contexto de rolagem e quebraria o `sticky` de dentro.
    <div
      className="bg-insigne-bg fundo-abertura min-h-screen pt-[var(--nav-h)] px-4 md:px-8 pb-16 text-white overflow-x-clip"
      style={{ "--altura-cabecalho-perfil": (esconderCabecalho ? 0 : alturaCabecalho) + "px" }}
    >
      {/* --topo-perfil: a borda de baixo do cabeçalho do site NESTE instante —
          a mesma conta de /livros (ver GridProduto.js). Fica aqui, e não na
          barra, porque as abas de dentro também a usam para grudar logo abaixo
          dela (ver MeusLivros). */}
      <div className="max-w-[1280px] mx-auto md:[--topo-perfil:max(0px,calc(var(--nav-h)_+_var(--nav-topo)_-_40px))]">
        {/* Breadcrumb */}
        <nav className="text-sm mb-8 flex gap-2 items-center">
          <button onClick={() => setActiveTab("dados")} className="text-insigne-orange hover:underline">
            Minha Conta
          </button>
          <span className="text-white/40">›</span>
          <span className="text-white/70">{TAB_LABELS[activeTab] || "Meu Perfil"}</span>
        </nav>

        {/* Sentinela: marca onde a barra NASCE na página. É por ela que se
            sabe se a barra está grudada, e não pela posição da própria barra —
            a barra é deslocada por `transform` quando se esconde, e um
            `getBoundingClientRect` nela mediria a posição do disfarce. */}
        <div ref={sentinelaRef} aria-hidden="true" className="h-0" />

        {/* ===== A BARRA DO NOME ACOMPANHA A ROLAGEM (desktop) =====
            Pedido do dono em 10/09/2026, "assim como é em Autores e Livros": a
            mesma conta de --topo-fixo daquelas duas páginas (ver o comentário
            longo em GridProduto.js), escrita direto no `top` porque aqui só
            UM bloco gruda — lá são dois, e a variável existe para os dois não
            desalinharem.

            `md:-mt-4 md:pt-4`: o fundo sobe 16px além do texto e encosta na
            borda da janela. Com o respiro no `top`, esses 16px ficariam
            transparentes e o conteúdo apareceria rolando pela fresta.

            A margem de baixo (`mb-8`) é MARGEM, fora do fundo: margem não
            pinta, então nada do conteúdo fica escondido atrás de uma faixa
            opaca maior que a própria barra.

            Só do tablet para cima. No celular esta linha quebra em duas ou três
            (selos e saldos), e presa no topo ela comeria um terço da tela.

            ===== O FUNDO VAI DE PONTA A PONTA =====
            O fundo opaco cobria só a coluna de 1280px, e o conteúdo aparecia
            rolando nas sobras laterais (print do dono em 11/09/2026). O
            `::before` estica o fundo por 100vw a partir do meio da barra, sem
            mexer no conteúdo dela — e a página ganhou `overflow-x-clip` por
            causa dos ~15px da barra de rolagem que o 100vw inclui.

            ===== ELA SOME QUANDO O CABEÇALHO DO SITE VOLTA =====
            Rolando para baixo, o cabeçalho do site sai e esta barra assume o
            topo. Rolando para CIMA, o cabeçalho volta — e duas barras
            empilhadas comeriam um terço da janela. Então, enquanto o cabeçalho
            estiver à vista E esta barra estiver grudada, ela desliza para fora
            com a mesma duração do cabeçalho. No alto da página (barra ainda no
            lugar dela, não grudada) nada disso vale: ali as duas convivem
            porque a barra está na posição natural. */}
        <div
          ref={cabecalhoRef}
          className={"mb-8 md:sticky md:z-30 md:-mt-4 md:pt-4 md:bg-insigne-bg " +
                     "md:top-[var(--topo-perfil)] " +
                     "md:before:absolute md:before:inset-y-0 md:before:left-1/2 md:before:-ml-[50vw] " +
                     "md:before:w-[100vw] md:before:bg-insigne-bg md:before:-z-10 " +
                     "md:transition-[top,transform,opacity] md:duration-[380ms] md:ease-in-out " +
                     "md:motion-reduce:transition-none " +
                     (esconderCabecalho ? "md:-translate-y-[150%] md:opacity-0 md:pointer-events-none" : "")}
        >
        {/* Cabeçalho: avatar + nome + selos + saldos */}
        <div className="flex items-center gap-3 md:gap-4 mb-8 flex-wrap">
          <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-line bg-insigne-navy-soft flex items-center justify-center shrink-0">
            {foto ? (
              <img src={foto} alt={userData?.nome} className="w-full h-full object-cover" />
            ) : (
              <MarcaPadrao />
            )}
          </div>
          <h1 className="titulo-display mb-0 text-[2.4rem] md:text-[3rem]">{userData?.nome || "Minha Conta"}</h1>

          {isAuthor && (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold
                             text-ink-fixed bg-gradient-to-r from-[#f0a23b] to-[#d97b1a] shadow-md">
              <i className="bi bi-book-half" />
              {seloFeminino ? "Autora Insigne" : "Autor Insigne"}
            </span>
          )}

          {/* ===== O selo do Clube =====
              Irmão do selo de autor — mesma forma, mesmo peso —, só que na
              paleta VERMELHA, a cor do Ticket: é com ele que se entra no Clube,
              e a mesma cor nos dois diz que são a mesma coisa.

              NA COR DO CLUBE (17/09/2026). Era um degradê vermelho escrito à
              mão (#DC3545 a #A61B29), fora do token — e por isso ficou
              vermelho quando o Clube inteiro passou para o branco. Agora é o
              `--c-ticket` com o `--c-ticket-contraste` por cima: branco com
              texto escuro no tema escuro, cinza com texto branco no claro.

              Só aparece com assinatura ATIVA. Selo de algo que já venceu seria
              prometer um acesso que a pessoa não tem mais. */}
          {clube?.ativo && (
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold
                         bg-insigne-ticket text-insigne-ticket-contraste shadow-md"
              title={clube.dias_restantes > 0 ? `Clube ativo por mais ${clube.dias_restantes} dias` : "Clube ativo"}
            >
              <i className="bi bi-ticket-perforated-fill" />
              Clube Insigne
            </span>
          )}

          {!isAuthor && (
            <button
              onClick={tornarSeAutor}
              disabled={promocaoLoading}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold
                         border border-insigne-orange/40 text-insigne-orange hover:bg-insigne-orange/10
                         transition-colors disabled:opacity-60"
            >
              <i className="bi bi-stars" />
              {promocaoLoading ? "Processando..." : "Tornar-se Autor"}
            </button>
          )}

          {/* ===== Os saldos, na PONTA DIREITA da barra =====
              Pedido do dono em 10/09/2026. Eles já moraram entre o nome e o
              selo de autor; agora nome e selos (quem a pessoa É) ficam juntos à
              esquerda, e os saldos (o que ela TEM) vão para o outro extremo.

              `md:ml-auto` e não `ml-auto`: no celular o cabeçalho quebra em
              linhas (`flex-wrap`), e ali empurrar para a direita deixaria os
              saldos sozinhos na ponta de uma linha vazia. Abaixo de 768px eles
              seguem os selos na ordem natural. */}
          <SaldoCreditos
            className="md:ml-auto"
            onComprar={() => setActiveTab("creditos")}
            onCarregar={(d) => setClube(d?.clube || null)}
          />
        </div>

        <div className="h-px bg-white/10" />
        </div>

        {/* Sidebar + Conteúdo */}
        {/* `md:gap-8`: o menu encolheu para 220px, e 40px de vão entre ele e o
            conteúdo devolviam ao vazio o espaço que o encolhimento liberou. */}
        <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start">
          <Sidebar activeTab={activeTab} onTabChange={setActiveTab} isAuthor={isAuthor} />
          <div className="flex-1 min-w-0 w-full">{renderSection()}</div>
        </div>

        {/* Logout discreto ao final */}
        <div className="mt-8 flex justify-end">
          <Logout onLogout={handleLogout} />
        </div>
      </div>
    </div>
  );
};

export default Perfil;
